import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import clsx from "clsx";
import LanguageSwitcher from "../components/LanguageSwitcher";

/* -------------------------------------------------------------------------- */
/*  Deterministic sample data so the visualisation is stable + SSR-friendly.  */
/* -------------------------------------------------------------------------- */

interface GrowthPoint {
  day: string;
  followers: number;
}
interface ActionPoint {
  day: string;
  storyViews: number;
  likes: number;
  follows: number;
  dms: number;
  comments: number;
}

function buildGrowthSeries(): GrowthPoint[] {
  const start = 1240;
  const end = 4820;
  const days = 30;
  const out: GrowthPoint[] = [];
  for (let i = 0; i < days; i++) {
    const t = i / (days - 1);
    const eased = 1 - Math.pow(1 - t, 1.7);
    const jitter = Math.sin(i * 1.7) * 18 + Math.cos(i * 0.9) * 11;
    const value = Math.round(start + (end - start) * eased + jitter);
    const d = new Date();
    d.setDate(d.getDate() - (days - 1 - i));
    out.push({
      day: `${d.getMonth() + 1}/${d.getDate()}`,
      followers: value,
    });
  }
  return out;
}

function buildActionSeries(): ActionPoint[] {
  const days = 14;
  const out: ActionPoint[] = [];
  for (let i = 0; i < days; i++) {
    const base = 1 + i * 0.06;
    const wobble = Math.sin(i * 1.3) * 12 + Math.cos(i * 0.6) * 8;
    const d = new Date();
    d.setDate(d.getDate() - (days - 1 - i));
    out.push({
      day: `${d.getMonth() + 1}/${d.getDate()}`,
      storyViews: Math.max(40, Math.round(180 * base + wobble * 2)),
      likes: Math.max(20, Math.round(95 * base + wobble * 1.5)),
      follows: Math.max(8, Math.round(38 * base + wobble * 0.6)),
      dms: Math.max(4, Math.round(22 * base + wobble * 0.3)),
      comments: Math.max(2, Math.round(14 * base + wobble * 0.2)),
    });
  }
  return out;
}

/* -------------------------------------------------------------------------- */
/*  SEO: keep the document head in sync with the active language.             */
/* -------------------------------------------------------------------------- */

function useLandingSeo(): void {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const title = t("landing.seo.title");
    const description = t("landing.seo.description");
    const keywords = t("landing.seo.keywords");
    document.title = title;

    const ensure = (selector: string, create: () => HTMLElement): HTMLElement => {
      const existing = document.head.querySelector<HTMLElement>(selector);
      if (existing) return existing;
      const el = create();
      document.head.appendChild(el);
      return el;
    };

    const meta = (name: string, content: string): void => {
      const el = ensure(`meta[name="${name}"]`, () => {
        const m = document.createElement("meta");
        m.setAttribute("name", name);
        return m;
      });
      el.setAttribute("content", content);
    };

    const ogMeta = (property: string, content: string): void => {
      const el = ensure(`meta[property="${property}"]`, () => {
        const m = document.createElement("meta");
        m.setAttribute("property", property);
        return m;
      });
      el.setAttribute("content", content);
    };

    meta("description", description);
    meta("keywords", keywords);
    ogMeta("og:title", title);
    ogMeta("og:description", description);
    ogMeta("og:type", "website");
    ogMeta("og:locale", (i18n.resolvedLanguage || "en").startsWith("es") ? "es_ES" : "en_US");
    meta("twitter:card", "summary_large_image");
    meta("twitter:title", title);
    meta("twitter:description", description);
  }, [t, i18n.resolvedLanguage]);
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function Landing(): JSX.Element {
  useLandingSeo();
  const { t } = useTranslation();
  const growth = useMemo(buildGrowthSeries, []);
  const actions = useMemo(buildActionSeries, []);
  const followerDelta = growth[growth.length - 1].followers - growth[0].followers;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <LandingHeader />
      <main>
        <Hero />
        <KpiStrip delta={followerDelta} />
        <TrustBar />
        <FeaturesSection />
        <DemoSection
          growth={growth}
          actions={actions}
          followerDelta={followerDelta}
        />
        <HowItWorksSection />
        <TestimonialsSection />
        <PricingSection />
        <FaqSection />
        <FinalCta />
      </main>
      <LandingFooter />
      <SchemaOrgJsonLd faqIds={["q1", "q1b", "q2", "q3", "q4", "q5"]} />
      {/* Decorative gradient blobs */}
      <div className="pointer-events-none fixed inset-x-0 top-0 -z-10 overflow-hidden">
        <div className="mx-auto h-[480px] max-w-6xl">
          <div className="absolute -top-32 left-1/4 h-72 w-72 rounded-full bg-brand-600/30 blur-3xl" />
          <div className="absolute -top-20 right-10 h-64 w-64 rounded-full bg-fuchsia-500/20 blur-3xl" />
        </div>
      </div>
      <span className="sr-only">{t("brand.name")}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Header                                                                     */
/* -------------------------------------------------------------------------- */

function LandingHeader(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const links = [
    { href: "#features", label: t("landing.nav.features") },
    { href: "#how-it-works", label: t("landing.nav.howItWorks") },
    { href: "#pricing", label: t("landing.nav.pricing") },
    { href: "#faq", label: t("landing.nav.faq") },
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-white/5 bg-slate-950/80 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <Link to="/" className="flex items-center gap-2" aria-label={t("brand.name")}>
          <LogoMark />
          <span className="text-sm font-semibold sm:text-base">{t("brand.name")}</span>
        </Link>

        <nav
          aria-label="Primary"
          className="hidden items-center gap-6 text-sm text-slate-300 md:flex"
        >
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="transition hover:text-white"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden lg:block">
            <LanguageSwitcher />
          </div>
          <Link
            to="/login"
            className="hidden text-sm text-slate-300 hover:text-white sm:inline"
          >
            {t("landing.nav.signIn")}
          </Link>
          <Link to="/signup" className="btn-primary px-3 py-2 text-sm sm:px-4">
            {t("landing.nav.getStarted")}
          </Link>
          <button
            type="button"
            className="rounded-lg p-2 text-slate-300 hover:bg-white/5 md:hidden"
            aria-label={open ? t("landing.nav.closeMenu") : t("landing.nav.openMenu")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <IconClose /> : <IconMenu />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      <div
        className={clsx(
          "overflow-hidden border-t border-white/5 transition-[max-height] duration-300 md:hidden",
          open ? "max-h-96" : "max-h-0"
        )}
      >
        <div className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3 text-sm">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="rounded-lg px-3 py-2.5 text-slate-200 hover:bg-white/5"
              onClick={() => setOpen(false)}
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/login"
            className="rounded-lg px-3 py-2.5 text-slate-200 hover:bg-white/5"
            onClick={() => setOpen(false)}
          >
            {t("landing.nav.signIn")}
          </Link>
          <div className="px-3 py-2">
            <LanguageSwitcher variant="block" />
          </div>
        </div>
      </div>
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/*  Hero                                                                       */
/* -------------------------------------------------------------------------- */

function Hero(): JSX.Element {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 pb-16 pt-10 sm:px-6 sm:pt-16 lg:grid-cols-[1.05fr_1fr] lg:gap-14 lg:pb-24 lg:pt-20">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-400/30 bg-brand-500/10 px-3 py-1 text-xs font-medium text-brand-200">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-300" />
            {t("landing.hero.eyebrow")}
          </span>
          <h1 className="mt-5 text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            <span className="block text-white">{t("landing.hero.titlePre")}</span>
            <span className="mt-1 block bg-gradient-to-r from-brand-300 via-fuchsia-300 to-pink-300 bg-clip-text text-transparent">
              {t("landing.hero.titleHighlight")}
            </span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            {t("landing.hero.subtitle")}
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link to="/signup" className="btn-primary px-5 py-3 text-base">
              {t("landing.hero.ctaPrimary")}
              <IconArrowRight />
            </Link>
            <a href="#how-it-works" className="btn-secondary px-5 py-3 text-base">
              {t("landing.hero.ctaSecondary")}
            </a>
          </div>
          <p className="mt-4 flex items-start gap-2 text-xs text-slate-400 sm:text-sm">
            <IconShield />
            <span>{t("landing.hero.note")}</span>
          </p>
        </div>

        <div className="relative">
          <HeroVisual />
        </div>
      </div>
    </section>
  );
}

function HeroVisual(): JSX.Element {
  const { t } = useTranslation();
  const data = useMemo(() => {
    return Array.from({ length: 24 }, (_, i) => {
      const t = i / 23;
      const eased = 1 - Math.pow(1 - t, 1.6);
      const wobble = Math.sin(i * 1.6) * 6 + Math.cos(i * 0.7) * 4;
      return { i, v: Math.round(20 + eased * 100 + wobble) };
    });
  }, []);

  return (
    <div className="relative">
      <div className="absolute -inset-6 rounded-3xl bg-gradient-to-tr from-brand-600/30 via-fuchsia-500/20 to-transparent blur-2xl" />
      <div className="relative rounded-3xl border border-white/10 bg-slate-900/70 p-4 shadow-2xl backdrop-blur sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-brand-400 to-fuchsia-500 ring-2 ring-white/10" />
            <div>
              <div className="text-sm font-semibold">@your.artist.name</div>
              <div className="text-xs text-slate-400">{t("landing.demo.previewBadge")}</div>
            </div>
          </div>
          <span className="pill bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-400/30">
            <span className="mr-1 h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-300" />
            Live
          </span>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <MiniStat label={t("landing.demo.legend.storyViews")} value="2,418" />
          <MiniStat label={t("landing.demo.legend.follows")} value="312" />
          <MiniStat label={t("landing.demo.legend.dms")} value="184" />
        </div>

        <div className="mt-4 h-36 sm:h-44">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="heroGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#d138ff" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#d138ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke="#e26aff"
                strokeWidth={2}
                fill="url(#heroGrad)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-400" />
            <span>+312 {t("landing.demo.legend.follows").toLowerCase()} · 30d</span>
          </div>
          <span>↑ 28%</span>
        </div>
      </div>

      <div className="absolute -bottom-4 -left-4 hidden rounded-2xl border border-white/10 bg-slate-900/90 p-3 text-xs shadow-xl sm:block">
        <div className="flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-full bg-brand-500/20 text-brand-200">
            <IconHeart />
          </div>
          <div>
            <div className="font-medium text-slate-100">DM sent to @newfan</div>
            <div className="text-slate-400">2 seconds ago</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-xl border border-white/5 bg-slate-950/60 p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 text-base font-semibold tabular-nums sm:text-lg">{value}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  KPI strip                                                                  */
/* -------------------------------------------------------------------------- */

function KpiStrip({ delta }: { delta: number }): JSX.Element {
  const { t } = useTranslation();
  const items = [
    { label: t("landing.kpis.followersAdded"), value: `+${delta.toLocaleString()}` },
    { label: t("landing.kpis.storyViewsDay"), value: "300–500" },
    { label: t("landing.kpis.dmsSent"), value: "50k+" },
    { label: t("landing.kpis.uptime"), value: "99.9%" },
  ];
  return (
    <section aria-label="Key metrics" className="border-y border-white/5 bg-slate-900/30">
      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 px-4 py-8 sm:grid-cols-4 sm:px-6">
        {items.map((it) => (
          <div key={it.label} className="text-center sm:text-left">
            <div className="bg-gradient-to-r from-brand-200 to-fuchsia-300 bg-clip-text text-2xl font-bold text-transparent sm:text-3xl">
              {it.value}
            </div>
            <div className="mt-1 text-xs text-slate-400 sm:text-sm">{it.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Trust bar                                                                  */
/* -------------------------------------------------------------------------- */

function TrustBar(): JSX.Element {
  const { t } = useTranslation();
  const items = [
    t("landing.trust.items.i1"),
    t("landing.trust.items.i2"),
    t("landing.trust.items.i3"),
    t("landing.trust.items.i4"),
    t("landing.trust.items.i5"),
    t("landing.trust.items.i6"),
  ];
  return (
    <section aria-label="Trusted by" className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <p className="text-center text-xs uppercase tracking-[0.2em] text-slate-500">
        {t("landing.trust.label")}
      </p>
      <ul className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-slate-400 sm:gap-x-10">
        {items.map((label) => (
          <li key={label} className="flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-400/60" />
            <span className="font-medium">{label}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Features                                                                   */
/* -------------------------------------------------------------------------- */

interface FeatureItem {
  key: string;
  icon: ReactNode;
}

function FeaturesSection(): JSX.Element {
  const { t } = useTranslation();
  const features: FeatureItem[] = [
    { key: "stories", icon: <IconEye /> },
    { key: "follows", icon: <IconUserPlus /> },
    { key: "welcome", icon: <IconChat /> },
    { key: "comments", icon: <IconSpark /> },
    { key: "caps", icon: <IconShieldCheck /> },
    { key: "multi", icon: <IconStack /> },
  ];

  return (
    <section
      id="features"
      aria-labelledby="features-title"
      className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20"
    >
      <SectionHeading
        eyebrow={t("landing.features.eyebrow")}
        titleId="features-title"
        title={t("landing.features.title")}
        subtitle={t("landing.features.subtitle")}
      />
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {features.map((f) => (
          <article
            key={f.key}
            className="group relative overflow-hidden rounded-2xl border border-white/5 bg-slate-900/50 p-5 transition hover:border-brand-400/40 hover:bg-slate-900/80"
          >
            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-brand-500/10 blur-2xl transition group-hover:bg-brand-500/20" />
            <div className="relative">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500/20 to-fuchsia-500/10 text-brand-200 ring-1 ring-white/10">
                {f.icon}
              </div>
              <h3 className="mt-4 text-lg font-semibold text-white">
                {t(`landing.features.items.${f.key}.title`)}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">
                {t(`landing.features.items.${f.key}.desc`)}
              </p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Demo (sample charts)                                                       */
/* -------------------------------------------------------------------------- */

function DemoSection({
  growth,
  actions,
  followerDelta,
}: {
  growth: GrowthPoint[];
  actions: ActionPoint[];
  followerDelta: number;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <section
      aria-labelledby="demo-title"
      className="border-y border-white/5 bg-gradient-to-b from-slate-900/40 via-slate-950 to-slate-950"
    >
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <SectionHeading
          eyebrow={t("landing.demo.eyebrow")}
          titleId="demo-title"
          title={t("landing.demo.title")}
          subtitle={t("landing.demo.subtitle")}
        />

        <div className="mt-10 overflow-hidden rounded-3xl border border-white/10 bg-slate-900/60 shadow-2xl">
          <div className="flex flex-col gap-2 border-b border-white/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-rose-400/80" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-300/80" />
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
              <span className="ml-3 text-sm text-slate-300">
                {t("landing.demo.previewHeading")}
              </span>
            </div>
            <span className="pill bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-400/30">
              <span className="mr-1 h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-300" />
              {t("landing.demo.previewBadge")}
            </span>
          </div>

          <div className="grid gap-6 p-4 sm:p-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-white/5 bg-slate-950/60 p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-slate-200">
                  {t("landing.demo.followerGrowthTitle")}
                </h3>
                <span className="text-xs font-semibold text-emerald-300">
                  {t("landing.demo.delta", { count: followerDelta })}
                </span>
              </div>
              <div className="mt-3 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={growth} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <defs>
                      <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#d138ff" stopOpacity={0.55} />
                        <stop offset="100%" stopColor="#d138ff" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis
                      dataKey="day"
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      interval={Math.ceil(growth.length / 6)}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      width={40}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#0f172a",
                        border: "1px solid #334155",
                        borderRadius: 8,
                        color: "#e2e8f0",
                        fontSize: 12,
                      }}
                      labelStyle={{ color: "#94a3b8" }}
                    />
                    <Area
                      type="monotone"
                      dataKey="followers"
                      stroke="#e26aff"
                      strokeWidth={2.5}
                      fill="url(#growthGrad)"
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-2xl border border-white/5 bg-slate-950/60 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-medium text-slate-200">
                  {t("landing.demo.dailyActionsTitle")}
                </h3>
                <ChartLegend />
              </div>
              <div className="mt-3 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={actions} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis
                      dataKey="day"
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      width={40}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#0f172a",
                        border: "1px solid #334155",
                        borderRadius: 8,
                        color: "#e2e8f0",
                        fontSize: 12,
                      }}
                      cursor={{ fill: "#1e293b66" }}
                    />
                    <Bar dataKey="storyViews" stackId="a" fill="#60a5fa" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="likes" stackId="a" fill="#a78bfa" />
                    <Bar dataKey="follows" stackId="a" fill="#f472b6" />
                    <Bar dataKey="dms" stackId="a" fill="#34d399" />
                    <Bar dataKey="comments" stackId="a" fill="#fbbf24" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <p className="border-t border-white/5 px-4 py-3 text-center text-xs text-slate-500 sm:px-6">
            {t("landing.demo.footnote")}
          </p>
        </div>
      </div>
    </section>
  );
}

function ChartLegend(): JSX.Element {
  const { t } = useTranslation();
  const series: Array<{ color: string; label: string }> = [
    { color: "#60a5fa", label: t("landing.demo.legend.storyViews") },
    { color: "#a78bfa", label: t("landing.demo.legend.likes") },
    { color: "#f472b6", label: t("landing.demo.legend.follows") },
    { color: "#34d399", label: t("landing.demo.legend.dms") },
    { color: "#fbbf24", label: t("landing.demo.legend.comments") },
  ];
  return (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
      {series.map((s) => (
        <li key={s.label} className="flex items-center gap-1.5">
          <span
            className="inline-block h-2 w-2 rounded-sm"
            style={{ backgroundColor: s.color }}
          />
          {s.label}
        </li>
      ))}
    </ul>
  );
}

/* -------------------------------------------------------------------------- */
/*  How it works                                                               */
/* -------------------------------------------------------------------------- */

function HowItWorksSection(): JSX.Element {
  const { t } = useTranslation();
  const steps = ["s1", "s2", "s3"] as const;
  return (
    <section
      id="how-it-works"
      aria-labelledby="how-it-works-title"
      className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20"
    >
      <SectionHeading
        eyebrow={t("landing.howItWorks.eyebrow")}
        titleId="how-it-works-title"
        title={t("landing.howItWorks.title")}
        subtitle={t("landing.howItWorks.subtitle")}
      />
      <ol className="mt-10 grid gap-4 sm:grid-cols-3">
        {steps.map((s, i) => (
          <li
            key={s}
            className="relative rounded-2xl border border-white/5 bg-slate-900/50 p-6"
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-fuchsia-500 text-sm font-bold text-white shadow-lg shadow-brand-900/40">
                {i + 1}
              </span>
              <h3 className="text-lg font-semibold">
                {t(`landing.howItWorks.steps.${s}.title`)}
              </h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              {t(`landing.howItWorks.steps.${s}.desc`)}
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Testimonials                                                               */
/* -------------------------------------------------------------------------- */

function TestimonialsSection(): JSX.Element {
  const { t } = useTranslation();
  const items = ["t1", "t2", "t3"] as const;
  return (
    <section
      aria-labelledby="testimonials-title"
      className="border-y border-white/5 bg-slate-900/30"
    >
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <SectionHeading
          eyebrow={t("landing.testimonials.eyebrow")}
          titleId="testimonials-title"
          title={t("landing.testimonials.title")}
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((id, idx) => (
            <figure
              key={id}
              className="relative rounded-2xl border border-white/5 bg-slate-950/60 p-6"
            >
              <IconQuote className="absolute right-4 top-4 text-brand-500/30" />
              <blockquote className="text-sm leading-relaxed text-slate-200">
                "{t(`landing.testimonials.items.${id}.quote`)}"
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                <div
                  className={clsx(
                    "h-10 w-10 rounded-full ring-2 ring-white/10",
                    idx === 0 && "bg-gradient-to-br from-pink-400 to-fuchsia-500",
                    idx === 1 && "bg-gradient-to-br from-amber-400 to-rose-500",
                    idx === 2 && "bg-gradient-to-br from-emerald-400 to-cyan-500"
                  )}
                />
                <div>
                  <div className="text-sm font-semibold text-white">
                    {t(`landing.testimonials.items.${id}.author`)}
                  </div>
                  <div className="text-xs text-slate-400">
                    {t(`landing.testimonials.items.${id}.role`)}
                  </div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Pricing                                                                    */
/* -------------------------------------------------------------------------- */

interface PlanDef {
  key: "solo" | "pro" | "label";
  featured?: boolean;
  features: ("f1" | "f2" | "f3" | "f4" | "f5")[];
}

function PricingSection(): JSX.Element {
  const { t } = useTranslation();
  const plans: PlanDef[] = [
    { key: "solo", features: ["f1", "f2", "f3", "f4", "f5"] },
    { key: "pro", featured: true, features: ["f1", "f2", "f3", "f4", "f5"] },
    { key: "label", features: ["f1", "f2", "f3", "f4", "f5"] },
  ];
  return (
    <section
      id="pricing"
      aria-labelledby="pricing-title"
      className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20"
    >
      <SectionHeading
        eyebrow={t("landing.pricing.eyebrow")}
        titleId="pricing-title"
        title={t("landing.pricing.title")}
        subtitle={t("landing.pricing.subtitle")}
      />
      <div className="mt-10 grid gap-4 sm:gap-5 lg:grid-cols-3">
        {plans.map((p) => (
          <div
            key={p.key}
            className={clsx(
              "relative flex flex-col rounded-2xl border p-6 transition",
              p.featured
                ? "border-brand-400/50 bg-gradient-to-b from-brand-500/10 to-slate-900/50 shadow-xl shadow-brand-900/30"
                : "border-white/5 bg-slate-900/50 hover:border-white/15"
            )}
          >
            {p.featured && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-brand-400 to-fuchsia-400 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-900">
                {t("landing.pricing.mostPopular")}
              </span>
            )}
            <h3 className="text-lg font-semibold">
              {t(`landing.pricing.plans.${p.key}.name`)}
            </h3>
            <p className="mt-1 text-sm text-slate-400">
              {t(`landing.pricing.plans.${p.key}.description`)}
            </p>
            <div className="mt-5 flex items-baseline gap-1">
              <span className="text-4xl font-bold tracking-tight text-white">
                {t(`landing.pricing.plans.${p.key}.price`)}
              </span>
              <span className="text-sm text-slate-400">{t("landing.pricing.perMonth")}</span>
            </div>
            <ul className="mt-5 space-y-2.5 text-sm text-slate-300">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <IconCheck className="mt-0.5 shrink-0 text-brand-300" />
                  <span>{t(`landing.pricing.plans.${p.key}.features.${f}`)}</span>
                </li>
              ))}
            </ul>
            <Link
              to={`/signup?plan=${p.key}`}
              className={clsx(
                "mt-7 w-full justify-center text-sm font-semibold",
                p.featured ? "btn-primary" : "btn-secondary"
              )}
            >
              {t(`landing.pricing.plans.${p.key}.cta`)}
            </Link>
            <p className="mt-2 text-center text-[11px] text-slate-500">
              {t("landing.pricing.trialNote")}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  FAQ                                                                        */
/* -------------------------------------------------------------------------- */

function FaqSection(): JSX.Element {
  const { t } = useTranslation();
  const ids = ["q1", "q1b", "q2", "q3", "q4", "q5"] as const;
  return (
    <section
      id="faq"
      aria-labelledby="faq-title"
      className="border-t border-white/5 bg-slate-900/30"
    >
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
        <SectionHeading
          eyebrow={t("landing.faq.eyebrow")}
          titleId="faq-title"
          title={t("landing.faq.title")}
          align="left"
        />
        <div className="mt-8 divide-y divide-white/5 rounded-2xl border border-white/5 bg-slate-950/40">
          {ids.map((id) => (
            <details
              key={id}
              className="group px-5 py-4 open:bg-white/5 [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-left text-base font-medium text-white">
                <span>{t(`landing.faq.items.${id}.q`)}</span>
                <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/5 text-slate-300 transition group-open:rotate-45">
                  <IconPlus />
                </span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                {t(`landing.faq.items.${id}.a`)}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Final CTA                                                                  */
/* -------------------------------------------------------------------------- */

function FinalCta(): JSX.Element {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="final-cta-title" className="px-4 py-16 sm:px-6 sm:py-24">
      <div className="relative mx-auto max-w-4xl overflow-hidden rounded-3xl border border-brand-400/30 bg-gradient-to-br from-brand-700/40 via-brand-600/20 to-slate-900 p-8 text-center sm:p-12">
        <div className="absolute -left-20 top-0 h-60 w-60 rounded-full bg-fuchsia-500/30 blur-3xl" />
        <div className="absolute -right-20 bottom-0 h-60 w-60 rounded-full bg-brand-500/30 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-300/40 bg-brand-500/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-brand-200">
            {t("landing.finalCta.eyebrow")}
          </span>
          <h2
            id="final-cta-title"
            className="mt-5 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl"
          >
            {t("landing.finalCta.title")}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-slate-300 sm:text-lg">
            {t("landing.finalCta.subtitle")}
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/signup" className="btn-primary px-6 py-3 text-base">
              {t("landing.finalCta.button")}
              <IconArrowRight />
            </Link>
            <Link to="/login" className="btn-secondary px-6 py-3 text-base">
              {t("landing.finalCta.secondary")}
            </Link>
          </div>
          <p className="mt-4 text-xs text-slate-400">{t("landing.finalCta.note")}</p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Footer                                                                     */
/* -------------------------------------------------------------------------- */

function LandingFooter(): JSX.Element {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-white/5 bg-slate-950">
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-2">
              <LogoMark />
              <span className="text-sm font-semibold">{t("brand.name")}</span>
            </div>
            <p className="mt-3 max-w-xs text-sm text-slate-400">{t("brand.tagline")}</p>
            <div className="mt-4">
              <LanguageSwitcher />
            </div>
          </div>
          <FooterCol
            title={t("landing.footer.product")}
            links={[
              { href: "#features", label: t("landing.footer.links.features") },
              { href: "#pricing", label: t("landing.footer.links.pricing") },
              { href: "#faq", label: t("landing.footer.links.faq") },
            ]}
          />
          <FooterCol
            title={t("landing.footer.company")}
            links={[
              { to: "/login", label: t("landing.footer.links.signIn") },
              { to: "/signup", label: t("landing.footer.links.signUp") },
              { href: "mailto:support@musoboost.com", label: t("landing.footer.links.contact") },
            ]}
          />
          <FooterCol
            title={t("landing.footer.legal")}
            links={[
              { href: "#", label: t("landing.footer.links.terms") },
              { href: "#", label: t("landing.footer.links.privacy") },
            ]}
          />
        </div>
        <div className="mt-10 flex flex-col gap-2 border-t border-white/5 pt-6 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <span>{t("landing.footer.copyright", { year })}</span>
          <span className="max-w-xl text-slate-500">{t("landing.footer.disclaimer")}</span>
        </div>
      </div>
    </footer>
  );
}

interface FooterLink {
  href?: string;
  to?: string;
  label: string;
}

function FooterCol({ title, links }: { title: string; links: FooterLink[] }): JSX.Element {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</div>
      <ul className="mt-3 space-y-2 text-sm">
        {links.map((l) => (
          <li key={l.label}>
            {l.to ? (
              <Link to={l.to} className="text-slate-300 hover:text-white">
                {l.label}
              </Link>
            ) : (
              <a href={l.href} className="text-slate-300 hover:text-white">
                {l.label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Reusable bits                                                              */
/* -------------------------------------------------------------------------- */

function SectionHeading({
  eyebrow,
  title,
  subtitle,
  titleId,
  align = "center",
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  titleId?: string;
  align?: "center" | "left";
}): JSX.Element {
  return (
    <div className={align === "center" ? "mx-auto max-w-2xl text-center" : "max-w-2xl"}>
      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-300">
        {eyebrow}
      </span>
      <h2
        id={titleId}
        className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl"
      >
        {title}
      </h2>
      {subtitle && (
        <p className="mt-3 text-base text-slate-400 sm:text-lg">{subtitle}</p>
      )}
    </div>
  );
}

function LogoMark(): JSX.Element {
  return (
    <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-fuchsia-600 shadow-lg shadow-brand-900/40">
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="white"
        aria-hidden="true"
      >
        <rect x="3.5" y="14" width="3" height="6.5" rx="1" />
        <rect x="9" y="10" width="3" height="10.5" rx="1" />
        <rect x="14.5" y="6" width="3" height="14.5" rx="1" />
        <circle cx="20" cy="4.7" r="2.2" />
      </svg>
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  JSON-LD                                                                    */
/* -------------------------------------------------------------------------- */

function SchemaOrgJsonLd({ faqIds }: { faqIds: readonly string[] }): JSX.Element {
  const { t, i18n } = useTranslation();
  const ids = faqIds;
  const lang = (i18n.resolvedLanguage || "en").split("-")[0];

  const data = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        name: t("brand.name"),
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description: t("landing.seo.description"),
        inLanguage: lang,
        audience: [
          { "@type": "Audience", audienceType: "Independent musicians" },
          { "@type": "Audience", audienceType: "Content creators" },
          { "@type": "Audience", audienceType: "Small businesses and brands" },
        ],
        offers: {
          "@type": "Offer",
          price: "19",
          priceCurrency: "USD",
        },
      },
      {
        "@type": "FAQPage",
        inLanguage: lang,
        mainEntity: ids.map((id) => ({
          "@type": "Question",
          name: t(`landing.faq.items.${id}.q`),
          acceptedAnswer: {
            "@type": "Answer",
            text: t(`landing.faq.items.${id}.a`),
          },
        })),
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

/* -------------------------------------------------------------------------- */
/*  Icons                                                                      */
/* -------------------------------------------------------------------------- */

interface IconProps {
  className?: string;
}

function svgProps(className?: string) {
  return {
    width: 20,
    height: 20,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    className,
  };
}

function IconMenu(): JSX.Element {
  return (
    <svg {...svgProps()}>
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}
function IconClose(): JSX.Element {
  return (
    <svg {...svgProps()}>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="6" y1="18" x2="18" y2="6" />
    </svg>
  );
}
function IconArrowRight(): JSX.Element {
  return (
    <svg {...svgProps()}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="13 6 19 12 13 18" />
    </svg>
  );
}
function IconShield(): JSX.Element {
  return (
    <svg {...svgProps("mt-0.5 text-brand-300")}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
function IconShieldCheck(): JSX.Element {
  return (
    <svg {...svgProps()}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}
function IconEye(): JSX.Element {
  return (
    <svg {...svgProps()}>
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function IconUserPlus(): JSX.Element {
  return (
    <svg {...svgProps()}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="16" y1="11" x2="22" y2="11" />
    </svg>
  );
}
function IconChat(): JSX.Element {
  return (
    <svg {...svgProps()}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function IconSpark(): JSX.Element {
  return (
    <svg {...svgProps()}>
      <path d="M12 2v6" />
      <path d="M12 16v6" />
      <path d="M2 12h6" />
      <path d="M16 12h6" />
      <path d="M5 5l3 3" />
      <path d="M16 16l3 3" />
      <path d="M19 5l-3 3" />
      <path d="M8 16l-3 3" />
    </svg>
  );
}
function IconStack(): JSX.Element {
  return (
    <svg {...svgProps()}>
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}
function IconHeart(): JSX.Element {
  return (
    <svg {...svgProps()}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}
function IconCheck({ className }: IconProps = {}): JSX.Element {
  return (
    <svg {...svgProps(className)}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function IconPlus(): JSX.Element {
  return (
    <svg {...svgProps()} width={14} height={14}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
function IconQuote({ className }: IconProps = {}): JSX.Element {
  return (
    <svg
      width={36}
      height={36}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M9.17 6C5.94 6 3.3 8.64 3.3 11.87V18h6V11.87H6.17C6.17 9.99 7.7 8.46 9.58 8.46H10V6h-.83zm10 0c-3.23 0-5.87 2.64-5.87 5.87V18h6V11.87h-3.13c0-1.88 1.53-3.41 3.41-3.41H20V6h-.83z" />
    </svg>
  );
}
